/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Cola de recordatorios de deuda vencida: una fila por cliente, cada envío es
 * un gesto del usuario (abre wa.me) — evita el bloqueo de popups del browser
 * y permite revisar/saltear casos antes de mandar.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { whatsAppHref } from '@shared/phone';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import type { CustomerAccountRow } from '../domain/customerTypes';
import {
  buildCustomerOverdueWhatsApp,
  formatOverdueDaysLabel,
  getDunningLevel,
  getOverdueDays,
} from '../application/customerDunning';
import {
  buildCustomerPaymentMessage,
  ensureCustomerPortalLink,
  openExternalUrl,
} from '../application/customerPortal';
import { formatMoney } from '../../shared/format/public-api';

type SendState = 'sending' | 'sent' | 'error';

type Props = {
  visible: boolean;
  /** Clientes vencidos (set completo, no la página visible). */
  customers: CustomerAccountRow[];
  paymentLinkAllowed: boolean;
  portalBaseUrl: string;
  onClose: () => void;
};

export const CustomerReminderQueueModal = ({
  visible,
  customers,
  paymentLinkAllowed,
  portalBaseUrl,
  onClose,
}: Props) => {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const [stateById, setStateById] = useState<Record<string, SendState>>({});

  // Estado fresco en cada apertura.
  useEffect(() => {
    if (visible) setStateById({});
  }, [visible]);

  const { sendable, noPhone } = useMemo(() => {
    const withPhone: CustomerAccountRow[] = [];
    const without: CustomerAccountRow[] = [];
    for (const c of customers) {
      if (whatsAppHref(c.phone, '')) withPhone.push(c);
      else without.push(c);
    }
    return { sendable: withPhone, noPhone: without };
  }, [customers]);

  const sentCount = useMemo(
    () => sendable.filter((c) => stateById[c.id] === 'sent').length,
    [sendable, stateById]
  );

  const sendOne = useCallback(
    async (customer: CustomerAccountRow) => {
      const days = getOverdueDays(customer) ?? 0;
      setStateById((prev) => ({ ...prev, [customer.id]: 'sending' }));
      try {
        let msg: string;
        if (paymentLinkAllowed) {
          try {
            const { paymentUrl } = await ensureCustomerPortalLink(customer.id, portalBaseUrl);
            msg = buildCustomerPaymentMessage(customer, paymentUrl);
          } catch {
            msg = buildCustomerOverdueWhatsApp({
              name: customer.name,
              balanceLabel: formatMoney(customer.balance),
              overdueDays: days,
              level: getDunningLevel(customer),
            });
          }
        } else {
          msg = buildCustomerOverdueWhatsApp({
            name: customer.name,
            balanceLabel: formatMoney(customer.balance),
            overdueDays: days,
            level: getDunningLevel(customer),
          });
        }
        const url = whatsAppHref(customer.phone, msg);
        if (!url) return;
        await openExternalUrl(url);
        setStateById((prev) => ({ ...prev, [customer.id]: 'sent' }));
      } catch {
        setStateById((prev) => ({ ...prev, [customer.id]: 'error' }));
      }
    },
    [paymentLinkAllowed, portalBaseUrl]
  );

  const header = (
    <TicketModalHeader
      kicker="Clientes"
      title="Recordar vencidos"
      subtitle={`${sendable.length} con WhatsApp${noPhone.length > 0 ? ` · ${noPhone.length} sin teléfono` : ''}`}
      onClose={onClose}
      compact={!isDesktop}
    />
  );

  const renderRow = useCallback(
    ({ item }: { item: CustomerAccountRow }) => {
      const days = getOverdueDays(item);
      const state = stateById[item.id];
      const level = getDunningLevel(item);
      const levelLabel = days != null ? `hace ${formatOverdueDaysLabel(days)}` : 'vencido';
      return (
        <View style={modalExperience.listRow}>
          <View style={styles.rowMain}>
            <Text style={modalExperience.listRowTitle} numberOfLines={1}>
              {item.name}
            </Text>
            <View style={styles.rowMeta}>
              <Text style={modalExperience.listMeta}>
                {formatMoney(item.balance)} · {levelLabel}
              </Text>
              {level !== 'none' && (
                <View style={[styles.levelBadge, level === 'r1' ? styles.levelWarn : styles.levelDanger]}>
                  <Text style={[styles.levelBadgeText, level === 'r1' ? styles.levelWarnText : styles.levelDangerText]}>
                    Vencido
                  </Text>
                </View>
              )}
            </View>
          </View>
          <TouchableOpacity
            style={[
              styles.sendBtn,
              state === 'sent' && styles.sendBtnSent,
              state === 'error' && styles.sendBtnError,
            ]}
            disabled={state === 'sending' || state === 'sent'}
            onPress={() => void sendOne(item)}
            accessibilityRole="button"
            accessibilityLabel={
              state === 'sent'
                ? `Recordatorio enviado a ${item.name}`
                : `Abrir WhatsApp para ${item.name}`
            }
            testID={`customers.reminder.send.${item.id}`}
          >
            {state === 'sending' ? (
              <ActivityIndicator size="small" color={ticketColors.brandInk} />
            ) : state === 'sent' ? (
              <>
                <Ionicons name="checkmark-circle" size={15} color={ticketColors.ok} />
                <Text style={[styles.sendBtnText, styles.sendBtnSentText]}>Enviado</Text>
              </>
            ) : (
              <>
                <Ionicons
                  name="logo-whatsapp"
                  size={15}
                  color={state === 'error' ? ticketColors.bad : ticketColors.brandInk}
                />
                <Text
                  style={[
                    styles.sendBtnText,
                    state === 'error' && styles.sendBtnErrorText,
                  ]}
                >
                  {state === 'error' ? 'Reintentar' : 'WhatsApp'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      );
    },
    [stateById, sendOne]
  );

  const renderNoPhoneRow = useCallback(
    ({ item }: { item: CustomerAccountRow }) => {
      const days = getOverdueDays(item);
      return (
        <View style={[modalExperience.listRow, styles.rowDisabled]}>
          <View style={styles.rowMain}>
            <Text style={[modalExperience.listRowTitle, styles.rowDisabledText]} numberOfLines={1}>
              {item.name}
            </Text>
            <Text style={modalExperience.listMetaMuted}>
              {formatMoney(item.balance)}
              {days != null ? ` · vencido hace ${formatOverdueDaysLabel(days)}` : ''} · sin teléfono
            </Text>
          </View>
          <View style={styles.noPhoneBadge}>
            <Text style={styles.noPhoneText}>Sin WhatsApp</Text>
          </View>
        </View>
      );
    },
    []
  );

  const rows = useMemo(
    () => [
      ...sendable.map((c) => ({ kind: 'sendable' as const, customer: c })),
      ...noPhone.map((c) => ({ kind: 'nophone' as const, customer: c })),
    ],
    [sendable, noPhone]
  );

  const footer = (
    <View style={styles.footer}>
      <Text style={styles.footerText}>
        {sentCount} de {sendable.length} enviado{sendable.length !== 1 ? 's' : ''}
      </Text>
      <TouchableOpacity
        style={posUi.primaryButton}
        onPress={onClose}
        accessibilityRole="button"
        accessibilityLabel="Cerrar"
        testID="customers.reminder.close"
      >
        <Text style={posUi.primaryButtonText}>
          {sentCount === sendable.length && sendable.length > 0 ? 'Listo' : 'Cerrar'}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const list = (
    <FlatList
      style={styles.sheetScroll}
      contentContainerStyle={styles.sheetScrollContent}
      data={rows}
      keyExtractor={(item) => item.customer.id}
      renderItem={(info) =>
        info.item.kind === 'sendable' ? renderRow({ item: info.item.customer }) : renderNoPhoneRow({ item: info.item.customer })
      }
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator
    />
  );

  if (isDesktop) {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <View style={[styles.sheetBackdrop, styles.backdropCenter]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <View style={[styles.sheetCard, styles.desktopCard]}>
            {header}
            <View style={styles.intro}>
              <Text style={modalExperience.listMeta}>
                Se abre un chat de WhatsApp por cliente. El mensaje queda listo — vos decidís si lo mandás.
              </Text>
            </View>
            {list}
            {footer}
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.sheetBackdrop}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View style={[styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
          <View style={styles.grabber} />
          {header}
          <View style={styles.intro}>
            <Text style={modalExperience.listMeta}>
              Se abre un chat de WhatsApp por cliente. El mensaje queda listo — vos decidís si lo mandás.
            </Text>
          </View>
          {list}
          {footer}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  sheetBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  backdropCenter: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  sheetCard: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 8,
    maxHeight: '92%',
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    zIndex: 1,
  },
  desktopCard: {
    width: '100%',
    maxWidth: 560,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingTop: 0,
  },
  grabber: {
    width: 28,
    height: 3,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginTop: 2,
    marginBottom: 12,
  },
  intro: {
    paddingHorizontal: 20,
    paddingBottom: spacing.sm,
  },
  sheetScroll: {
    flexShrink: 1,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.sm,
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  rowMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  levelBadge: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderWidth: 1,
  },
  levelWarn: {
    backgroundColor: ticketColors.warnBg,
    borderColor: ticketColors.warnBorder,
  },
  levelDanger: {
    backgroundColor: 'rgba(248, 113, 113, 0.10)',
    borderColor: 'rgba(248, 113, 113, 0.35)',
  },
  levelBadgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  levelWarnText: {
    color: ticketColors.warn,
  },
  levelDangerText: {
    color: ticketColors.bad,
  },
  sendBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 36,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  sendBtnSent: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.brandBorder,
  },
  sendBtnError: {
    borderColor: ticketColors.bad,
  },
  sendBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.brandInk,
  },
  sendBtnSentText: {
    color: ticketColors.ok,
  },
  sendBtnErrorText: {
    color: ticketColors.bad,
  },
  rowDisabled: {
    opacity: 0.6,
  },
  rowDisabledText: {
    color: ticketColors.ink2,
  },
  noPhoneBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
  },
  noPhoneText: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink3,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingHorizontal: 20,
    paddingVertical: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  footerText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
});
